import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SeoHead from "../components/SeoHead";
import { ChevronDown } from "lucide-react";
import { useSaunaBasePrice } from "../hooks/useSaunaBasePrice";

const models = [
  { name: "UMI", saunaKey: "umi", fallback: 182000, desc: "Liten og elegant. Perfekt når plassen er begrenset, men drømmen om en egen badstue ikke er mindre.", image: "https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/7e34399bd_Umi2.png", href: "/sauna-umi", size: "3–4 personer", dims: "255 × 271 × 330 cm", altText: "LYKKE UMI – kompakt badstue modell" },
  { name: "MORI", saunaKey: "mori", fallback: 219000, desc: "Det er noe helt særegent ved en dårlig proporsjoner. MORI er der, klassisk, rolig og tidløs.", image: "https://media.base44.com/images/public/69a726345560d5ef49a37111/9fe09ba1d_LYKKE_MORI_BLACK1.jpg", href: "/sauna-mori", size: "4–6 personer", dims: "359 × 255 × 330 cm", altText: "LYKKE MORI – klassisk badstue med naturlige proporsjoner" },
  { name: "HIRO", saunaKey: "hiro", fallback: 249000, desc: "Med innelukket forstue blir det en hel opplevelse. Varmt og klart å komme inn fra kulden.", image: "https://media.base44.com/images/public/69a726345560d5ef49a37111/3d5fe43e4_HIRO_1.jpg", href: "/sauna-hiro", size: "4–6 personer", dims: "619 × 255 × 330 cm", altText: "LYKKE HIRO – badstue med innelukket forstue" },
  { name: "YORI", saunaKey: "yori", fallback: 279000, desc: "En badstue som tenker på detaljer. Fra før du går inn til du kommer ut igjen.", image: "https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/5d996f8f7_ChatGPTImage12maj202612_14_43.png", href: "/sauna-yori", size: "4–6 personer", dims: "727 × 255 × 330 cm", altText: "LYKKE YORI – premium badstue med detaljer" },
  { name: "TORI", saunaKey: "tori", fallback: 319000, desc: "For dem som ikke gjør halvveis. En badstue der alt stemmer – design, kvalitet, og rommelighet.", image: "https://media.base44.com/images/public/69a726345560d5ef49a37111/75d0922d0_LYKKETORI1.jpg", href: "/sauna-tori", size: "8–12 personer", dims: "1013 × 255 × 330 cm", altText: "LYKKE TORI – stor premium badstue modell" },
];

function ModelCard({ model }) {
  const { basePrice, loading } = useSaunaBasePrice(model.saunaKey, model.fallback);
  return (
    <a href={model.href} className="group cursor-pointer block bg-secondary/30 border border-border/40 p-8 hover:border-border transition-all duration-300 hover:bg-secondary/50">
      <h3 className="font-heading text-3xl md:text-4xl font-light mb-3 transition-colors duration-300 group-hover:text-foreground/70">LYKKE {model.name}</h3>
      <p className="text-foreground/60 text-2xl leading-relaxed mb-6 w-full transition-colors duration-300 group-hover:text-foreground/40">{model.desc}</p>
      <div className="aspect-[4/3] overflow-hidden mb-6 bg-secondary/50 relative">
        <img
          src={model.image}
          alt={model.altText}
          className="w-full h-full object-cover group-hover:scale-105 transition-all duration-[1.5s]"
          width="800"
          height="600"
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="space-y-1.5 text-2xl font-body border-t border-border/40 pt-4 transition-colors duration-300">
        <p className="text-foreground/60 transition-colors duration-300 group-hover:text-foreground/50">Størrelse: <strong className="text-foreground transition-colors duration-300 group-hover:text-foreground/70">{model.size}</strong></p>
        <p className="text-foreground/60 transition-colors duration-300 group-hover:text-foreground/50">Dimensjoner: <strong className="text-foreground transition-colors duration-300 group-hover:text-foreground/70">{model.dims}</strong></p>
        <p className="text-foreground/60 transition-colors duration-300 group-hover:text-foreground/50">Fra: <strong className="text-foreground transition-colors duration-300 group-hover:text-foreground/70">{loading ? "..." : `${basePrice.toLocaleString("nb-NO")} NOK`}</strong></p>
      </div>
    </a>
  );
}

export default function SaunaCollectionLY() {
  return (
    <>
      <SeoHead
        title="LYKKE LY – Klassiske badstuer med saltaker | LYKKE NOMAD"
        description="LYKKE LY-kolleksjonen: UMI, MORI, HIRO, YORI og TORI – klassiske utendørsbadstuer med saltakform. Nordisk design, naturlige materialer, levering hele Norge."
        image="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/ac944b326_LYKKE_UMI_DETALMORI1.png"
        canonical="https://lykke.info/sauna-ly"
      />
      <Navbar />
      
      <section className="relative h-screen w-full overflow-hidden">
        <img
          src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/ac944b326_LYKKE_UMI_DETALMORI1.png"
          alt="LYKKE LY Kolekcja – klassiske badstuer med saltaker"
          className="absolute inset-0 w-full h-full object-cover"
          width="1920"
          height="1080"
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/10" />

        <div className="relative z-10 h-full flex flex-col justify-end pb-32 md:pb-40 px-6 md:px-12 max-w-[1440px] mx-auto">
          <p className="text-sm tracking-widest-2xl font-body uppercase text-white/50 mb-6">
            Koleksjon
          </p>
          <h1 className="font-heading text-white text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] mb-6">
            LYKKE LY
            <br />
            <span className="italic opacity-60">Tidløs klassiker</span>
          </h1>
          <p className="text-white/70 text-2xl md:text-2xl tracking-widest-2xl font-body uppercase">
            Klassiske tokkonstruksjoner - tidløs eleganse
          </p>
        </div>

        <button
          onClick={() => {
            document.querySelector("#models")?.scrollIntoView({ behavior: "smooth" });
          }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50 hover:text-white transition-colors animate-bounce"
        >
          <ChevronDown size={28} strokeWidth={1} />
        </button>
      </section>

      <section className="py-16 md:py-24 bg-secondary/50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl md:text-5xl font-light leading-[1.2] mb-6">
              Klassiske takformer
            </h2>
            <p className="font-body text-2xl leading-relaxed text-foreground/70">
              LYKKE LY er designet for å harmonere med naturen omkring. Valg av materialer, farger og takform gjør at badstuen blir en naturlig del av omgivelsene.
              <br />
              <br />
              Det levende grønne taket gjør sitt best både praktisk og estetisk – det isolerer bedre, håndterer regnvann, og mykner hele uttrykket.
              <br />
              <br />
              En løsning for steder hvor helhet og naturlig tilpasning betyr noe.
            </p>
          </div>
        </div>
      </section>

      <section id="models" className="py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-6">
            {models.map((model) => (
              <ModelCard key={model.name} model={model} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40 bg-secondary/50">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">
                God stemning
              </p>
              <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-light leading-[1.1] mb-8">
                Ekte
                <br />
                <span className="italic">materialer</span>
              </h2>
              <p className="font-body leading-relaxed text-foreground/70 mb-6 text-2xl">
                Alle LYKKE badstuer bygges i tre og naturlige materialer valgt for sin holdbarhet, varme og karakter.
                <br /><br />
                Når badstuen varmes opp, fylles rommet med en behagelig duft som skaper en rolig og avslappende stemning. Det er nettopp den følelsen vi elsker med en ekte badstue.
              </p>
            </div>
            <div className="aspect-[3/4] overflow-hidden rounded-lg bg-transparent flex items-center justify-center">
              <img src="https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/f4a2a3e59_vellykket-badstue_stacked-full_clay.png" alt="LYKKE LY – illustrasjon av alle badstuemodeller" className="w-full h-full object-contain" width="600" height="800" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-light mb-8">
            Finn badstuen
            <br />
            <span className="italic">som passer deg</span>
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => window.location.href = "/process"}
              className="text-base tracking-widest-xl font-body uppercase px-8 py-3.5 border border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300">
              BLI KJENT MED PROSESSEN
            </button>
            <button
              onClick={() => window.location.href = "/sauna-panorama"}
              className="text-base tracking-widest-xl font-body uppercase px-8 py-3.5 border border-foreground/30 text-foreground/70 hover:border-[#7a6f63] hover:bg-[#7a6f63] hover:text-white transition-all duration-300">
              LYKKE PANORAMA
            </button>
            <button
              onClick={() => window.location.href = "/sauna-fjel"}
              className="text-base tracking-widest-xl font-body uppercase px-8 py-3.5 border border-foreground/30 text-foreground/70 hover:border-[#7a6f63] hover:bg-[#7a6f63] hover:text-white transition-all duration-300">
              LYKKE FJELL
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}